import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Play, ArrowRight, X } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { useCatalog } from '../context/CatalogContext';

export function ContinueWatchingRow({ title = 'Continue Watching', limit = 4, showRemove = false }) {
  const navigate = useNavigate();
  const { progress, removeProgress } = useApp();
  const { byId } = useCatalog();
  const items = Object.values(progress)
    .sort((a, b) => b.updatedAt - a.updatedAt)
    .map((p) => ({ ...p, content: byId(p.contentId) }))
    .filter((p) => p.content && p.position < p.duration - 5)
    .slice(0, limit || undefined);
  if (!items.length) return null;
  return (
    <section className="pl-[22px] mt-[6px]" data-testid="continue-watching">
      <h2 className="row-title mb-[11px]">{title}</h2>
      <div className="flex gap-[20px] overflow-x-auto no-scrollbar pr-[40px] py-2 -my-2">
        {items.map((p) => {
          const pct = Math.round((p.position / p.duration) * 100);
          const dest = p.content.type === 'podcast' ? `/podcasts/${p.content.id}` : `/watch/${p.content.id}${p.episodeId ? `?ep=${p.episodeId}` : ''}`;
          return (
            <div key={p.contentId} className="cw-card" onClick={() => navigate(dest)} data-testid={`cw-${p.contentId}`} role="button">
              <img src={p.content.image} alt={p.content.title} loading="lazy" />
              <div className="shade" />
              <div className="absolute inset-y-0 left-[16px] flex items-center gap-[12px]">
                <span className="w-[36px] h-[36px] rounded-full border border-white/80 bg-black/40 flex items-center justify-center"><Play size={14} fill="currentColor" className="ml-0.5" /></span>
                <div>
                  <div className="text-[13px] font-bold leading-tight">{p.content.title}</div>
                  <div className="text-[11px] text-white/80 mt-[3px]">{p.label}</div>
                </div>
              </div>
              {showRemove && <button onClick={(e) => { e.stopPropagation(); removeProgress(p.contentId); }} className="absolute top-2 right-2 icon-btn !w-7 !h-7" aria-label="Remove"><X size={13} /></button>}
              <div className="progress-track"><div className="progress-fill" style={{ width: `${pct}%` }} /></div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

export function CategoryPanels() {
  const { homeCfg } = useApp();
  const categoryPanels = homeCfg.panels || [];
  if (!categoryPanels.length) return null;
  return (
    <section className="mt-[22px] grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4" data-testid="category-panels">
      {categoryPanels.map((p) => (
        <Link key={p.id} to={p.to} className="cat-panel" style={{ background: p.color }} data-testid={`panel-${p.id}`}>
          <img src={p.image} alt="" loading="lazy" style={{ objectPosition: p.pos || 'center top' }} />
          <div className="veil" style={{ background: `linear-gradient(90deg, ${p.color} 0%, ${p.color} 40%, ${p.color}00 72%), linear-gradient(0deg, rgba(0,0,0,0.35), transparent 40%)` }} />
          <div className="relative z-[1] pl-[26px] pr-[40%]">
            <h3 className="text-[20px] font-bold leading-[1.15] whitespace-pre-line">{p.title}</h3>
            <p className="text-[12.5px] leading-[1.4] text-white/85 mt-[8px] max-w-[190px]">{p.text}</p>
          </div>
          <span className="arrow"><ArrowRight size={15} /></span>
        </Link>
      ))}
    </section>
  );
}
